import * as coreSchemas from '@/lib/validation/store/core'
import { articleStateSchema } from '@/lib/validation/store/features/articles'
import { galleryStateSchema } from '@/lib/validation/store/features/gallery'
import { locationStateSchema } from '@/lib/validation/store/features/locations'
import { productStateSchema } from '@/lib/validation/store/features/products'
import { projectStateSchema } from '@/lib/validation/store/features/projects'
import { serviceStateSchema } from '@/lib/validation/store/features/services'
import { pubSidebarStateSchema } from '@/lib/validation/store/pub-sidebar'
import * as sharedSchemas from '@/lib/validation/store/shared'
import { Middleware, UnknownAction, isAction } from '@reduxjs/toolkit'
import { z } from 'zod'

const schemaMap = {
  // Core schemas
  alerts: coreSchemas.alertStateSchema,
  privacy: coreSchemas.privacyStateSchema,
  globalSettings: coreSchemas.globalSettingsStateSchema,

  // Feature schemas
  products: productStateSchema,
  services: serviceStateSchema,
  articles: articleStateSchema,
  locations: locationStateSchema,
  projects: projectStateSchema,
  gallery: galleryStateSchema,
  pubSidebar: pubSidebarStateSchema,

  // Shared schemas
  cart: sharedSchemas.cartStateSchema,
  recentlyViewed: sharedSchemas.recentlyViewedStateSchema,
}

// Map of which states are paginated vs filterable
const stateTypeMap: Record<string, 'paginated' | 'filterable' | 'base'> = {
  articles: 'paginated',
  gallery: 'paginated',
  products: 'filterable',
  services: 'filterable',
  locations: 'filterable',
  cart: 'base',
  alerts: 'base',
  privacy: 'base',
  globalSettings: 'base',
  pubSidebar: 'filterable',
  recentlyViewed: 'base'
}

interface ValidationErrorAction extends UnknownAction {
  type: 'validation/error'
  payload: Record<string, z.ZodIssue[]>
}

const isValidationErrorAction = (action: unknown): action is ValidationErrorAction => {
  return isAction(action) && action.type === 'validation/error'
}

const shouldSkipValidation = (action: unknown): boolean => {
  if (!isAction(action)) return false
  return (
    action.type === 'validation/error' ||
    action.type === 'persist/REHYDRATE' ||
    action.type === 'persist/PERSIST' ||
    // Skip validation for Redux init actions
    action.type.startsWith('@@redux/') ||
    // Skip validation for init actions
    action.type.includes('/init') ||
    action.type.includes('INIT') ||
    // Skip validation for loading states
    action.type.includes('/pending') ||
    action.type.includes('/loading') ||
    // Skip for specific actions that might have partial data
    action.type.includes('products/setSelectedProduct') ||
    action.type.includes('gallery/setSelectedItem') ||
    // Skip for cart-related actions until schema is fixed
    action.type.includes('cart/') ||
    // Skip for privacy/consent initialization
    action.type.includes('privacy/init') ||
    action.type === 'privacy/setCookieConsent' ||
    action.type === 'privacy/setMicrosoftIntegrationConsent' ||
    action.type === 'privacy/setGoogleIntegrationConsent' ||
    action.type === 'privacy/setFacebookIntegrationConsent'
  )
}

const hasBaseFields = (state: Record<string, unknown>): boolean => {
  return (
    'loading' in state &&
    'error' in state &&
    (typeof state.loading === 'boolean' || state.loading === null) &&
    (state.error === null || typeof state.error === 'string')
  )
}

const hasPaginationFields = (state: Record<string, unknown>): boolean => {
  return (
    'page' in state &&
    'limit' in state &&
    'total' in state &&
    (typeof state.page === 'number' || state.page === null) &&
    (typeof state.limit === 'number' || state.limit === null) &&
    (typeof state.total === 'number' || state.total === null)
  )
}

const hasFilterFields = (state: Record<string, unknown>): boolean => {
  return (
    'filterParams' in state &&
    (state.filterParams === null || typeof state.filterParams === 'object')
  )
}

const isInitialStateValid = (state: unknown, stateKey: string): boolean => {
  if (!state || typeof state !== 'object') return false
  const stateObj = state as Record<string, unknown>

  // Check base fields first
  if (!hasBaseFields(stateObj)) return false

  // Check additional fields based on state type
  const stateType = stateTypeMap[stateKey]
  switch (stateType) {
    case 'paginated':
      return hasPaginationFields(stateObj)
    case 'filterable':
      return hasFilterFields(stateObj)
    case 'base':
    default:
      return true
  }
}

export const stateValidationMiddleware: Middleware =
  (store) =>
    (next) =>
      (action) => {
        // Skip validation for certain actions
        if (shouldSkipValidation(action)) {
          return next(action)
        }

        // Run the action first
        const result = next(action)

        // Get the new state
        const state = store.getState()

        // Track validation errors
        const validationErrors: Record<string, z.ZodIssue[]> = {}

        // Validate each slice of state
        Object.entries(schemaMap).forEach(([key, schema]) => {
          // Skip validation if the state slice doesn't exist yet
          if (!state[key]) return

          // Skip validation for initial states that haven't been fully initialized
          if (!isInitialStateValid(state[key], key)) return

          try {
            schema.parse(state[key])
          } catch (error) {
            if (error instanceof z.ZodError) {
              validationErrors[key] = error.issues
              // Only log validation errors in development
              if (process.env.NODE_ENV === 'development') {
                console.error(`State validation error in ${key}:`, error.issues)
              }
            }
          }
        })

        // If there are any validation errors, dispatch a single validation error action
        if (Object.keys(validationErrors).length > 0) {
          store.dispatch({
            type: 'validation/error',
            payload: validationErrors
          } as ValidationErrorAction)
        }

        return result
      }
